import {
  Banner,
  BlockStack,
  Button,
  Card,
  InlineStack,
  Text,
} from "@shopify/polaris";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  applyPurchaseOrderHistory,
  applyStockAdjustmentHistory,
  dryRunPurchaseOrderHistory,
  dryRunStockAdjustmentHistory,
  type StockyHistoryImportResult,
  type StockyHistorySourceFile,
} from "../../api/stocky_history_import";
import { fileToBase64 } from "../../api/stocky_csv_import";
import { csvErrorMessage } from "../../components/csvImportSpec";
import { t } from "../../lib/i18n";

interface LoadedSource extends StockyHistorySourceFile {
  mediaType: string;
}

interface ConfirmedHistory {
  batchId: string;
  files: readonly LoadedSource[];
  result: StockyHistoryImportResult;
}

function newUuid(): string {
  return globalThis.crypto.randomUUID();
}

async function loadFiles(files: FileList | null): Promise<LoadedSource[]> {
  return Promise.all(
    Array.from(files ?? []).map(async (file) => ({
      sourceImportId: newUuid(),
      fileName: file.name,
      fileBase64: await fileToBase64(file),
      mediaType: file.type || "text/csv",
    })),
  );
}

function ResultDetails({ result }: { result: StockyHistoryImportResult }) {
  if (result.normalization_mode === "retention_only") {
    const retentionMessage =
      result.retention_reason === "hard_row_cap_exceeded"
        ? t(
            "CSV行数が運用データの安全上限100,000行を超えています。発注書・在庫調整の業務データへは変換せず、原本を完全保存します（{rows}行すべて未処理）。",
            { rows: result.unprocessed_rows.toLocaleString("en-US") },
          )
        : result.retention_reason === "hard_logical_record_cap_exceeded"
          ? t(
              "1件の発注書または在庫調整の明細が安全上限（25,000行または正規化後8MiB）を超えています。この取込全体を発注書・在庫調整の業務データへは変換せず、原本を完全保存します（{rows}行すべて未処理）。",
              { rows: result.unprocessed_rows.toLocaleString("en-US") },
            )
          : t(
              "運用データの安全上限により、この取込全体を発注書・在庫調整の業務データへは変換せず、原本を完全保存します（{rows}行すべて未処理）。",
              { rows: result.unprocessed_rows.toLocaleString("en-US") },
            );
    return (
      <BlockStack gap="100">
        <Text as="p">{retentionMessage}</Text>
        <Text as="p">
          {t(
            "保存後は、この画面の「保存済みデータの取り出し」から全行を汎用形式で分割取得できます。",
          )}
        </Text>
      </BlockStack>
    );
  }
  return (
    <Text as="p">
      {t(
        "{files} ファイル・元データ {rows} 行を確認しました。記録: 新規 {created} 件・更新 {updated} 件・変更なし {unchanged} 件。原本 {archived} ファイルを保管します。",
        {
          files: result.file_count,
          rows: result.source_rows,
          created: result.created,
          updated: result.updated,
          unchanged: result.unchanged,
          archived: result.archived_files,
        },
      )}
    </Text>
  );
}

function PurchaseOrderCoverage({
  result,
}: {
  result: StockyHistoryImportResult;
}) {
  return (
    <BlockStack gap="100">
      <Text as="p">
        {t(
          "発注書 {records} 件のうち、商品明細を正規化済み {complete} 件・商品明細未取得 {incomplete} 件・明細確認が必要 {conflicts} 件です。未取得の発注書も一覧情報と原本は保存され、個別CSVを後から追加できます。",
          {
            records: result.record_count,
            complete: result.detail_provided_records,
            incomplete: result.detail_not_provided_records,
            conflicts: result.detail_conflict_records,
          },
        )}
      </Text>
      {result.detail_conflict_records > 0 && (
        <Text as="p" tone="critical">
          {t(
            "親一覧と個別CSVの仕入先が一致しない発注書があります。個別CSVの原本は保存しますが、商品明細は推測で正規化しません。",
          )}
        </Text>
      )}
    </BlockStack>
  );
}

interface HistoryCardProps {
  kind: "purchase-orders" | "stock-adjustments";
  title: string;
  description: string;
  files: readonly LoadedSource[];
  filesLoading: boolean;
  canConfirm: boolean;
  onFilesChange: (files: FileList | null) => Promise<void>;
  parentInput?: {
    files: readonly LoadedSource[];
    loading: boolean;
    onChange: (files: FileList | null) => Promise<void>;
  };
  batchId: string;
  onImported: () => void;
}

function HistoryCard({
  kind,
  title,
  description,
  files,
  filesLoading,
  canConfirm,
  onFilesChange,
  parentInput,
  batchId,
  onImported,
}: HistoryCardProps) {
  const [confirmed, setConfirmed] = useState<ConfirmedHistory | null>(null);
  const [result, setResult] = useState<StockyHistoryImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const allFiles = useMemo(
    () => [...(parentInput?.files ?? []), ...files],
    [files, parentInput?.files],
  );
  const loadingFiles = filesLoading || (parentInput?.loading ?? false);

  const resetConfirmation = useCallback(() => {
    setConfirmed(null);
    setResult(null);
    setError(null);
  }, []);

  const changeFiles = useCallback(
    async (next: FileList | null) => {
      resetConfirmation();
      try {
        await onFilesChange(next);
      } catch (err) {
        setError(csvErrorMessage(err));
      }
    },
    [onFilesChange, resetConfirmation],
  );
  const changeParent = useCallback(
    async (next: FileList | null) => {
      resetConfirmation();
      try {
        await parentInput?.onChange(next);
      } catch (err) {
        setError(csvErrorMessage(err));
      }
    },
    [parentInput, resetConfirmation],
  );

  const dryRun = useCallback(async () => {
    if (!canConfirm || loadingFiles || allFiles.length === 0) return;
    const snapshot = { batchId, files: allFiles };
    setBusy(true);
    setError(null);
    try {
      const checked =
        kind === "purchase-orders"
          ? await dryRunPurchaseOrderHistory(snapshot)
          : await dryRunStockAdjustmentHistory(snapshot);
      if (!checked.dry_run || !checked.plan_token) {
        throw new Error(
          "dry-run response did not contain a confirmation token",
        );
      }
      setConfirmed({ ...snapshot, result: checked });
      setResult(null);
    } catch (err) {
      setError(csvErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }, [allFiles, batchId, canConfirm, kind, loadingFiles]);

  const apply = useCallback(async () => {
    if (!confirmed?.result.plan_token) return;
    setBusy(true);
    setError(null);
    try {
      const request = {
        batchId: confirmed.batchId,
        files: confirmed.files,
        planToken: confirmed.result.plan_token,
        confirmRetentionOnly:
          confirmed.result.normalization_mode === "retention_only",
      };
      const applied =
        kind === "purchase-orders"
          ? await applyPurchaseOrderHistory(request)
          : await applyStockAdjustmentHistory(request);
      setResult(applied);
      setConfirmed(null);
      onImported();
    } catch (err) {
      // 通信再試行では、確認済み batch/file UUID と plan token をそのまま再利用する。
      setError(csvErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }, [confirmed, kind, onImported]);

  return (
    <Card>
      <BlockStack gap="300">
        <BlockStack gap="100">
          <Text as="h2" variant="headingMd">
            {t(title)}
          </Text>
          <Text as="p" variant="bodySm" tone="subdued">
            {t(description)}
          </Text>
        </BlockStack>

        {parentInput && (
          <BlockStack gap="100">
            <Text as="p" fontWeight="semibold">
              {t("1. 親一覧 purchase_orders.csv（1ファイル）")}
            </Text>
            <input
              type="file"
              accept=".csv,text/csv"
              aria-label={t("発注書の親一覧CSV")}
              disabled={busy}
              onChange={(event) => void changeParent(event.target.files)}
            />
            {parentInput.files.map((file) => (
              <Text
                key={file.sourceImportId}
                as="p"
                variant="bodySm"
                tone="subdued"
              >
                {file.fileName}
              </Text>
            ))}
          </BlockStack>
        )}

        <BlockStack gap="100">
          <Text as="p" fontWeight="semibold">
            {parentInput
              ? t("2. purchase_order_<PO番号>.csv（複数選択可）")
              : t("選ぶファイル: stock_adjustments.csv（複数選択可）")}
          </Text>
          <input
            type="file"
            multiple
            accept=".csv,text/csv"
            aria-label={
              parentInput ? t("個別の発注書CSV") : t("在庫調整の履歴CSV")
            }
            disabled={busy}
            onChange={(event) => void changeFiles(event.target.files)}
          />
          {files.map((file) => (
            <Text
              key={file.sourceImportId}
              as="p"
              variant="bodySm"
              tone="subdued"
            >
              {file.fileName}
            </Text>
          ))}
        </BlockStack>

        {parentInput && parentInput.files.length === 1 && files.length === 0 && (
          <Banner tone="warning" title={t("親一覧だけを先に保存できます")}>
            <Text as="p">
              {t(
                "商品明細は「未取得」として保存します。個別の発注書CSVが見つかったときは、同じ親一覧と一緒に後から追加できます。",
              )}
            </Text>
          </Banner>
        )}

        <InlineStack align="end">
          <Button
            variant="primary"
            disabled={!canConfirm || busy || loadingFiles}
            loading={busy || loadingFiles}
            onClick={confirmed ? apply : dryRun}
          >
            {confirmed?.result.normalization_mode === "retention_only"
              ? t("業務データへ変換せず原本を保存することを確認しました")
              : confirmed
                ? t("確定して取り込む")
                : t("件数を確認する")}
          </Button>
        </InlineStack>

        {confirmed && (
          <Banner
            tone="warning"
            title={
              confirmed.result.normalization_mode === "retention_only"
                ? t(
                    "まだ保存していません。業務データへ変換せず原本を保存する内容を確認してください",
                  )
                : t("まだ保存していません。件数と原本を確認してください")
            }
          >
            <BlockStack gap="100">
              <ResultDetails result={confirmed.result} />
              {parentInput && confirmed.result.domain_normalization_complete && (
                <PurchaseOrderCoverage
                  result={confirmed.result}
                />
              )}
            </BlockStack>
          </Banner>
        )}
        {error && (
          <Banner tone="critical" title={t("取り込みに失敗しました")}>
            <Text as="p">{error}</Text>
          </Banner>
        )}
        {result && (
          <Banner
            tone={
              result.domain_normalization_complete ? "success" : "warning"
            }
            title={
              result.domain_normalization_complete
                ? t("履歴と原本を保存しました")
                : t("原本を保存しました（運用データの正規化は未完了）")
            }
          >
            <BlockStack gap="100">
              <ResultDetails result={result} />
              {parentInput && result.domain_normalization_complete && (
                <PurchaseOrderCoverage
                  result={result}
                />
              )}
            </BlockStack>
          </Banner>
        )}
      </BlockStack>
    </Card>
  );
}

interface Props {
  onImported: () => void;
}

/** API が使えない店の PO parent/detail と adjustment CSV の正規化取込。 */
export function StockyHistoryImport({ onImported }: Props) {
  const [poBatchId, setPoBatchId] = useState(newUuid);
  const [poParent, setPoParent] = useState<LoadedSource[]>([]);
  const [poParentLoading, setPoParentLoading] = useState(false);
  const [poDetails, setPoDetails] = useState<LoadedSource[]>([]);
  const [poDetailsLoading, setPoDetailsLoading] = useState(false);
  const [adjustmentBatchId, setAdjustmentBatchId] = useState(newUuid);
  const [adjustments, setAdjustments] = useState<LoadedSource[]>([]);
  const [adjustmentsLoading, setAdjustmentsLoading] = useState(false);
  const mounted = useRef(true);
  const poParentGeneration = useRef(0);
  const poDetailsGeneration = useRef(0);
  const adjustmentsGeneration = useRef(0);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      poParentGeneration.current += 1;
      poDetailsGeneration.current += 1;
      adjustmentsGeneration.current += 1;
    };
  }, []);

  const changePoParent = useCallback(async (files: FileList | null) => {
    const generation = ++poParentGeneration.current;
    setPoBatchId(newUuid());
    setPoParent([]);
    setPoParentLoading(true);
    try {
      const loaded = await loadFiles(files);
      if (mounted.current && generation === poParentGeneration.current) {
        setPoParent(loaded);
      }
    } finally {
      if (mounted.current && generation === poParentGeneration.current) {
        setPoParentLoading(false);
      }
    }
  }, []);
  const changePoDetails = useCallback(async (files: FileList | null) => {
    const generation = ++poDetailsGeneration.current;
    setPoBatchId(newUuid());
    setPoDetails([]);
    setPoDetailsLoading(true);
    try {
      const loaded = await loadFiles(files);
      if (mounted.current && generation === poDetailsGeneration.current) {
        setPoDetails(loaded);
      }
    } finally {
      if (mounted.current && generation === poDetailsGeneration.current) {
        setPoDetailsLoading(false);
      }
    }
  }, []);
  const changeAdjustments = useCallback(async (files: FileList | null) => {
    const generation = ++adjustmentsGeneration.current;
    setAdjustmentBatchId(newUuid());
    setAdjustments([]);
    setAdjustmentsLoading(true);
    try {
      const loaded = await loadFiles(files);
      if (mounted.current && generation === adjustmentsGeneration.current) {
        setAdjustments(loaded);
      }
    } finally {
      if (mounted.current && generation === adjustmentsGeneration.current) {
        setAdjustmentsLoading(false);
      }
    }
  }, []);

  return (
    <BlockStack gap="400">
      <HistoryCard
        kind="purchase-orders"
        title={t("発注書・明細")}
        description={t("Stockyで Reports → Purchase Orders → CSV を押し、purchase_orders.csvを1つ選びます。次に Purchases から各発注書を開き、Download → CSV で取得した purchase_order_<PO番号>.csvを選びます。Received Only / Not Received Onlyは使いません。個別CSVがない発注書は、明細未取得のまま親一覧と原本を保存できます。")}
        parentInput={{
          files: poParent,
          loading: poParentLoading,
          onChange: changePoParent,
        }}
        files={poDetails}
        filesLoading={poDetailsLoading}
        onFilesChange={changePoDetails}
        batchId={poBatchId}
        canConfirm={poParent.length === 1}
        onImported={onImported}
      />
      <HistoryCard
        kind="stock-adjustments"
        title={t("在庫調整・明細")}
        description={t("Stockyで Reports → Adjustments → CSV を押し、stock_adjustments.csvを選びます。期間や拠点を分けて書き出した場合は、取得したCSVをすべて選びます。一般の在庫調整と棚卸し履歴は別データです。")}
        files={adjustments}
        filesLoading={adjustmentsLoading}
        onFilesChange={changeAdjustments}
        batchId={adjustmentBatchId}
        canConfirm={adjustments.length > 0}
        onImported={onImported}
      />
    </BlockStack>
  );
}
